import React from "react";
import styled from "styled-components";

const skeletonLoading = `
  @keyframes skeleton-loading {
    0% {
      background-color: #f0f0f0;
    }
    100% {
      background-color: #e0e0e0;
    }
  }
`;

const Skeleton = styled.div`
  ${skeletonLoading}
  animation: skeleton-loading 1.5s infinite alternate;
  border-radius: 4px;
`;

const SkeletonCard = styled.div`
  background: #fff;
  border: 1px solid #eee;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 20px;
`;

const SkeletonHeader = styled.div`
  background: #f9f9f9;
  padding: 20px;
  border-bottom: 1px solid #eee;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const SkeletonHeaderItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SkeletonText = styled(Skeleton)`
  height: 12px;
  width: 60px;

  &.large {
    height: 16px;
    width: 100%;
    max-width: 120px;
  }
`;

const SkeletonBody = styled.div`
  padding: 20px;
`;

const SkeletonItemRow = styled.div`
  display: grid;
  grid-template-columns: 80px 1fr;
  gap: 16px;
  padding: 12px;
  background: #f9f9f9;
  border-radius: 6px;
  margin-bottom: 12px;

  @media (max-width: 768px) {
    grid-template-columns: 70px 1fr;
    gap: 12px;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const SkeletonItemImage = styled(Skeleton)`
  width: 80px;
  height: 80px;

  @media (max-width: 768px) {
    width: 70px;
    height: 70px;
  }
`;

const SkeletonItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SkeletonSection = styled.div`
  padding: 16px;
  background: #f9f9f9;
  border-radius: 6px;
  margin-bottom: 20px;
`;

const SkeletonFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-top: 1px solid #eee;
  background: #fff;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }
`;

const SkeletonOrderCard = ({ count = 1 }) => {
  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <SkeletonCard key={index}>
          <SkeletonHeader>
            <SkeletonHeaderItem>
              <SkeletonText />
              <SkeletonText className="large" />
            </SkeletonHeaderItem>
            <SkeletonHeaderItem>
              <SkeletonText />
              <SkeletonText className="large" />
            </SkeletonHeaderItem>
            <SkeletonHeaderItem>
              <SkeletonText />
              <SkeletonText className="large" />
            </SkeletonHeaderItem>
            <SkeletonHeaderItem>
              <SkeletonText />
              <SkeletonText className="large" />
            </SkeletonHeaderItem>
          </SkeletonHeader>

          <SkeletonBody>
            {/* Items Section */}
            <div style={{ marginBottom: "24px" }}>
              <SkeletonText
                className="large"
                style={{ marginBottom: "16px" }}
              />
              {Array.from({ length: 2 }).map((_, itemIndex) => (
                <SkeletonItemRow key={itemIndex}>
                  <SkeletonItemImage />
                  <SkeletonItemInfo>
                    <SkeletonText className="large" />
                    <SkeletonText style={{ width: "80px" }} />
                    <SkeletonText style={{ width: "100px" }} />
                  </SkeletonItemInfo>
                </SkeletonItemRow>
              ))}
            </div>

            {/* Address Section */}
            <SkeletonSection>
              <SkeletonText
                className="large"
                style={{ marginBottom: "12px" }}
              />
              <SkeletonText style={{ marginBottom: "8px" }} />
              <SkeletonText style={{ marginBottom: "8px" }} />
              <SkeletonText style={{ width: "120px" }} />
            </SkeletonSection>
          </SkeletonBody>

          <SkeletonFooter>
            <div style={{ width: "100px" }}>
              <SkeletonText style={{ marginBottom: "8px" }} />
              <SkeletonText className="large" />
            </div>
            <div style={{ display: "flex", gap: "8px", width: "100%" }}>
              <SkeletonText style={{ width: "100px", height: "36px" }} />
              <SkeletonText style={{ width: "100px", height: "36px" }} />
              <SkeletonText style={{ width: "100px", height: "36px" }} />
            </div>
          </SkeletonFooter>
        </SkeletonCard>
      ))}
    </>
  );
};

export default SkeletonOrderCard;
